/**
 * Shared drag-and-drop mixin for moderation modules.
 *
 * DRAG SOURCE (left column):
 *   Import as `dragSourceMixin` and provide `dragIdField` in data
 *   to specify which item field to track (e.g., 'id_colaboracao', 'cod_noticia', 'id').
 *
 * DROP TARGET (right column):
 *   Import as `dropTargetMixin`.
 *   Override `onDropAction(item)` in the component to handle the API call.
 */

function readViewAccent(sourceEl) {
  // Probe technique: getComputedStyle doesn't resolve chained var() refs on
  // custom properties, so we set a real CSS property (color) to var(--view-accent)
  // on a temporary child of the source element, then read the resolved color.
  if (!sourceEl) return '#00E074';
  const probe = document.createElement('div');
  probe.style.cssText = 'position:absolute;visibility:hidden;color:var(--view-accent)';
  sourceEl.appendChild(probe);
  const resolved = getComputedStyle(probe).color;
  sourceEl.removeChild(probe);
  return resolved || '#00E074';
}

function withAlpha(color, alpha) {
  // color comes from getComputedStyle as `rgb(r, g, b)` — turn it into rgba(...).
  const match = color.match(/\d+/g);
  if (match && match.length >= 3) {
    return `rgba(${match[0]}, ${match[1]}, ${match[2]}, ${alpha})`;
  }
  return color;
}

function createDragGhost(evt, item, sourceEl) {
  const color = readViewAccent(sourceEl);
  const bgTint = withAlpha(color, 0.12);
  const ghost = document.createElement('div');
  ghost.className = 'drag-ghost';
  ghost.style.borderColor = color;

  const title = item.dsc_nome_colaborador
    || item.dsc_titulo_resumido
    || item.dsc_titulo_enquete
    || item.dsc_titulo
    || item.nome
    || 'Item';

  const source = item.dsc_fonte_colaboracao
    || item.cod_portal
    || item.tenant_id_backstage
    || '';

  ghost.innerHTML = `
    <div class="drag-ghost__icon" style="background: ${bgTint}; color: ${color}">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <path d="M10 9h4V6h3l-5-5-5 5h3v3zm-1 1H6V7l-5 5 5 5v-3h3v-4zm14 2l-5-5v3h-3v4h3v3l5-5zm-9 3h-4v3H7l5 5 5-5h-3v-3z"/>
      </svg>
    </div>
    <div class="drag-ghost__text">
      <span class="drag-ghost__title">${title.length > 35 ? title.substring(0, 35) + '…' : title}</span>
      ${source ? `<span class="drag-ghost__source">${source}</span>` : ''}
    </div>
  `;

  document.body.appendChild(ghost);
  evt.dataTransfer.setDragImage(ghost, 20, 20);

  // Clean up after browser captures the image
  requestAnimationFrame(() => {
    setTimeout(() => {
      if (ghost.parentNode) ghost.parentNode.removeChild(ghost);
    }, 0);
  });
}

export const dragSourceMixin = {
  data() {
    return {
      draggingId: null,
    };
  },
  methods: {
    startDrag(evt, item) {
      const idField = this.dragIdField || 'id';
      this.draggingId = item[idField];
      document.body.classList.add('is-dragging');
      evt.dataTransfer.dropEffect = 'move';
      evt.dataTransfer.effectAllowed = 'move';
      evt.dataTransfer.setData('text/plain', JSON.stringify(item));
      // sourceEl gives createDragGhost a place to probe --view-accent.
      // evt.currentTarget is the element with the @dragstart listener (the card).
      createDragGhost(evt, item, evt.currentTarget || evt.target);
      // A successful drop can remove the source node (optimistic updates), so the
      // source's own `dragend` may never fire and the drag state would stick —
      // leaving the item looking "held" if it later returns to the list. Listen for
      // the drop at the document level as a reliable fallback to reset the state.
      this._onDocumentDrop = () => this.endDrag();
      document.addEventListener('drop', this._onDocumentDrop, { once: true });
    },
    endDrag() {
      this.draggingId = null;
      document.body.classList.remove('is-dragging');
      if (this._onDocumentDrop) {
        document.removeEventListener('drop', this._onDocumentDrop);
        this._onDocumentDrop = null;
      }
    },
  },
};

export const dropTargetMixin = {
  data() {
    return {
      isDragOver: false,
      lastDroppedId: null,
    };
  },
  methods: {
    handleDragOver() {
      this.isDragOver = true;
    },
    handleDragLeave() {
      this.isDragOver = false;
    },
    async handleDrop(evt) {
      this.isDragOver = false;

      try {
        const rawData = evt.dataTransfer.getData('text/plain');
        if (!rawData) {
          console.error('No drag data received');
          return;
        }

        let item;
        try {
          item = JSON.parse(rawData);
        } catch (parseError) {
          console.error('Failed to parse drag data:', parseError);
          this.$toast.error('Erro ao processar dados arrastados');
          return;
        }

        const selectedProducao = this.$store.state.producoes.selectedProducao;
        if (!selectedProducao || !selectedProducao.cod_producao) {
          console.error('No production selected');
          this.$toast.error('Nenhuma produção selecionada');
          return;
        }

        // Track for drop animation
        const idField = item.id_colaboracao ? 'id_colaboracao'
          : item.cod_noticia ? 'cod_noticia'
          : 'id';
        this.lastDroppedId = item[idField];

        await this.onDropAction(item, selectedProducao);

        // Clear animation after it plays
        setTimeout(() => { this.lastDroppedId = null; }, 600);
      } catch (error) {
        console.error('Error in handleDrop:', error);
        const msg = error.response?.data?.error || 'Erro ao mover item para exibição';
        this.$toast.error(msg);
        this.lastDroppedId = null;
      }
    },
    // Override in component
    async onDropAction(/* item, selectedProducao */) {
      console.warn('onDropAction not implemented');
    },
  },
};
